import React, { FC, memo, useMemo, useContext, useEffect, useCallback, useState } from 'react';
import { Field, FormikProps, useFormikContext } from 'formik';
import getTime from 'date-fns/getTime';
import Grid from '@mui/material/Grid';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormControl from '@mui/material/FormControl';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import { currencyIdOf } from '../../../Reservations/TravelFile/TravelFileHomePage/AccountingSection/GeneratePdfs/Repay/repaidAmounts';

import Spinner from '../../../../components/Spinner';
import { LoaderOnComponent } from '../../../../components/Loader/Loader';
import RenderInput from '../../../../components/RenderInput';
import { displayToast } from '../../../../components/RTToast';
import { taytanFormatNumberToString } from '../../../../utils/mathUtils';
import { isDateValid } from '../../../../utils/dateUtils';
import { sameCurrency } from '../../../../utils/currencyMath';
import { Context as ExchangeRatesContext } from '../../../../api/exchangeRates/context';
import { Context as ClientsContext } from '../../../../api/clients/context';
import { Context as CurrenciesContext } from '../../../../api/currencies/context';
import {
    calcVatOnTop,
    getVatPercent,
    paymentMethodTypeEnum,
    PaymentMethodTypes,
    paymentMethodTypesOptionsWithoutVoucher
} from '../../../../api/vouchers/helpers';
import CreditCardSubForm from '../subForms/CreditCardSubForm';
import CreditCardPaymentPlanFields from '../subForms/CreditCardPaymentPlanFields';
import PaymentsTerminalField from '../../../../components/PaymentsTerminalField';
import RepayCardDetails from './RepayCardDetails';
import { Client } from '../../../../api/clients/types';
import { Currency } from '../../../../api/currencies/types';
import { Voucher } from '../../../../api/vouchers/types';
import usePaymentPlanInfo from '../../../../hooks/usePaymentPlanInfo';
import FormFieldArray from '../../../../components/FormFieldArray';
import RowCheckFields, {
    getCheckLocalAmountLabel,
    getCheckSummary,
    getEmptyCheck
} from '../../../../subForms/RowCheckFields/RowCheckFields';
import AmountCurrencyRateBox from '../subForms/AmountCurrencyRateBox';

import CollapsibleSection from '../../../../components/CollapsibleSection/CollapsibleSection';
import remittanceFormStyles from './remittanceFormStyles';
import RemittanceBankTransferSubForm from './RemittanceBankTransferSubForm';

type Props = {
    setPaymentMethodType: React.Dispatch<React.SetStateAction<PaymentMethodTypes>>;
    documentGenerationData: Partial<Voucher> | null;
    open: boolean;
    toggleOpen: () => void;
    isNextToMasleka: boolean;
    setIsNextToMasleka: (flag: boolean) => void;
    setIsPaidWithCreditCard: (flag: boolean) => void;
    // Passed straight to the Masleka card sub-form so it can persist the payload
    // and issue the Remittance after the gateway returns to the former page.
    selectedDocuments: Array<Voucher> | null;
    // Inline card flow: issue the Remittance in place once the charge is
    // approved, so the PDF opens without reloading the page.
    onCardPaymentSuccess?: (values: any) => void;
    // On a repay from a receipt: what was already paid back against it, and what is left.
    repayAllowance?: {
        receiptAmount: number;
        alreadyRepaid: number;
        remaining: number;
        currencyId: string | null;
    } | null;
};
// Form component:
const RemittanceForm: FC<Props> = ({
    setPaymentMethodType,
    documentGenerationData,
    open,
    toggleOpen,
    isNextToMasleka,
    setIsNextToMasleka,
    setIsPaidWithCreditCard,
    selectedDocuments,
    onCardPaymentSuccess,
    repayAllowance
}: Props) => {
    const formProps = useFormikContext() as FormikProps<any>;
    const { setFieldValue, isSubmitting, values } = formProps;
    const classes = remittanceFormStyles();

    const [loadingText, setLoadingText] = useState<string>('');
    const justResetRef = React.useRef(false);

    const { getConvertRate } = useContext(ExchangeRatesContext);
    const exchangeRatesContext = useContext(ExchangeRatesContext);

    const clientsContext = useContext(ClientsContext);
    const currenciesContext = useContext(CurrenciesContext);

    const currencySettings = useMemo(() => {
        return (clientsContext.selected as Client)?.currencySettings;
    }, [clientsContext.selected]);

    const localCurrency: string = currencySettings?.localCurrency as string;
    // Pelecard (the Masleka gateway) only ever settles in ILS or USD — restrict the
    // currency picker to just those two while Credit Card is the payment method,
    // matching tourRegistrationApp's rules (docs/tour-registration-payment-rules.md).
    const ilsUsdCurrencyOptions = useMemo(
        () => currenciesContext.dataAsArray.filter((c: Currency) => c.code === 'ILS' || c.code === 'USD'),
        [currenciesContext.dataAsArray]
    );
    const { tripDate, fullPaymentWindowDays } = usePaymentPlanInfo(documentGenerationData?.travelFileId);

    // ── Cheque remittances: the amount IS the sum of the cheques ─────────────
    // Same rule as the receipt forms. Without it the Amount field and the cheque
    // rows were entered separately and could silently disagree — the document
    // would save and print the typed Amount while the rows showed something else.
    const checksTotal = useMemo<number>(() => {
        if (values.paymentMethodType !== paymentMethodTypeEnum.CHECK) return 0;
        const checks = values.paymentMethodObject?.checks;
        if (!Array.isArray(checks)) return 0;
        return (
            Math.round(
                checks.reduce((sum: number, check: any) => sum + (Number(check?.amount) || 0), 0) * 100
            ) / 100
        );
    }, [values.paymentMethodType, values.paymentMethodObject?.checks]);

    useEffect(() => {
        if (values.paymentMethodType !== paymentMethodTypeEnum.CHECK) return;
        if (checksTotal !== Number(values.amount)) setFieldValue('amount', checksTotal);
    }, [checksTotal, values.paymentMethodType]); // eslint-disable-line react-hooks/exhaustive-deps

    // The amount prefilled from the covered documents (netToRemit total). Warn —
    // never block — when the cheques don't settle it in full.
    const coveredTotal: number = Number(formProps.initialValues?.amount) || 0;
    const checksMismatch =
        values.paymentMethodType === paymentMethodTypeEnum.CHECK &&
        Array.isArray(values.paymentMethodObject?.checks) &&
        values.paymentMethodObject.checks.length > 0 &&
        coveredTotal > 0 &&
        checksTotal !== coveredTotal;

    const onClose = () => {
        setIsNextToMasleka(false);
        toggleOpen();
    };

    useEffect(() => {
        if (open) {
            justResetRef.current = true;
            formProps.resetForm();
        }
    }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        setIsPaidWithCreditCard(values.paymentMethodType === paymentMethodTypeEnum.CREDIT_CARD);
    }, [values.paymentMethodType]);

    const getNewRateByDateToChange = useCallback(
        async (date: Date | null) => {
            const currId = typeof values.currency === 'object' ? values.currency?._id : values.currency;
            // A local-currency remittance has no document→local rate to look up:
            // getConvertRate(₪, ₪) finds no row and falls back to 1, which would
            // overwrite the local↔PRIMARY rate RateControlBox keeps in `rate`
            // for this case (and that `rate` is what the saved document and its
            // PDF use). The box already tracks the value date itself.
            if (sameCurrency(currId, localCurrency)) return undefined;
            const dateTimeStamp = getTime(new Date(date || new Date()));
            setLoadingText('Calc Rate...');
            return exchangeRatesContext
                .get({ date: dateTimeStamp })
                .then((res) => {
                    const rate = getConvertRate(currId, localCurrency, res);
                    const rateByDate: number = (rate as { exchangeRateSell: number })?.exchangeRateSell || 1;
                    if (rateByDate) {
                        setFieldValue('rate', rateByDate);
                        setLoadingText('');
                        return rateByDate;
                    } else {
                        const rate2 = getConvertRate(currId, localCurrency);
                        const todayRate: number =
                            (rate2 as { exchangeRateSell: number })?.exchangeRateSell || 1;
                        setFieldValue('rate', todayRate);
                        setLoadingText('');
                        return todayRate;
                    }
                })
                .catch(() => {
                    displayToast('error', 'Error to get updated rate!', '');
                    setLoadingText('');
                    return undefined;
                });
        },
        [exchangeRatesContext, getConvertRate, values.currency, localCurrency, setFieldValue]
    );

    const prevValueDateRef = React.useRef<string | null>(null);
    useEffect(() => {
        if (justResetRef.current) {
            justResetRef.current = false;
            return;
        }
        // Respect the "Override rate by value date" toggle — skip rate fetch when off
        if (!values.paymentMethodObject.overrideRateByValueDate) return;
        const date = values.paymentMethodObject.valueDate;
        const dateStr = date ? new Date(date).toISOString().slice(0, 10) : null;
        // Only fetch rate when the date actually changes (not on mount/reset)
        if (prevValueDateRef.current === null) {
            prevValueDateRef.current = dateStr;
            return;
        }
        if (dateStr === prevValueDateRef.current) return;
        prevValueDateRef.current = dateStr;
        if (date === null || isDateValid(date)) {
            getNewRateByDateToChange(date);
        }
    }, [values.paymentMethodObject.valueDate, values.paymentMethodObject.overrideRateByValueDate]); // eslint-disable-line

    return (
        <>
            <Box p={3} className={classes.positionRelative}>
                {loadingText && <LoaderOnComponent text={loadingText} />}
                {repayAllowance &&
                    (() => {
                        // Amounts are in the receipt's currency; only compare like with like.
                        const symbol = currenciesContext.getSymbolByCurrency(repayAllowance.currencyId as any) || '';
                        const sameCurrency =
                            !repayAllowance.currencyId || currencyIdOf(values.currency) === repayAllowance.currencyId;
                        const over = sameCurrency && Number(values.amount) > repayAllowance.remaining;
                        if (repayAllowance.alreadyRepaid <= 0 && !over) return null;
                        return (
                            <Alert severity={over ? 'warning' : 'info'} sx={{ mb: 2 }}>
                                {`Already repaid from this receipt: ${symbol}${taytanFormatNumberToString(
                                    repayAllowance.alreadyRepaid
                                )}. Left to repay: ${symbol}${taytanFormatNumberToString(repayAllowance.remaining)}.`}
                                {over && ' The amount is more than is left to repay.'}
                            </Alert>
                        );
                    })()}
                <Grid container spacing={4} justifyContent="space-between">
                    <Grid container item direction="column" spacing={2} xs={12} sm={6}>
                        <CollapsibleSection label="Remittance Details" card>
                            <Stack spacing={2}>
                                <Field
                                    component={RenderInput}
                                    label="For"
                                    name="for"
                                    disabled={isSubmitting}
                                    fullWidth
                                    margin="none"
                                />
                                <Field
                                    component={RenderInput}
                                    label="Beneficiary"
                                    name={values.travelAgentName ? 'travelAgentName' : 'supplierName'}
                                    disabled
                                    fullWidth
                                    margin="none"
                                />
                                <AmountCurrencyRateBox
                                    documentGenerationData={documentGenerationData}
                                    disabled={isSubmitting}
                                    // For cheques the figure belongs to the rows below.
                                    hideAmount={values.paymentMethodType === paymentMethodTypeEnum.CHECK}
                                    rateDateOverride={(() => {
                                        if (!values.paymentMethodObject.overrideRateByValueDate) return null;
                                        if (
                                            values.paymentMethodType === paymentMethodTypeEnum.BANK_TRANSFER
                                        ) {
                                            return values.paymentMethodObject.valueDate;
                                        }
                                        if (values.paymentMethodType === paymentMethodTypeEnum.CHECK) {
                                            return values.paymentMethodObject?.checks?.[0]?.valueDate || null;
                                        }
                                        return null;
                                    })()}
                                    disableRateControl={
                                        (values.paymentMethodType === paymentMethodTypeEnum.BANK_TRANSFER ||
                                            values.paymentMethodType === paymentMethodTypeEnum.CHECK) &&
                                        !!values.paymentMethodObject.overrideRateByValueDate
                                    }
                                    currencyOptionsOverride={
                                        values.paymentMethodType === paymentMethodTypeEnum.CREDIT_CARD
                                            ? ilsUsdCurrencyOptions
                                            : undefined
                                    }
                                />
                                {/* Charge VAT — only for Shekel (local-currency) remittances, e.g.
                                    guide salary where the guide is עוסק מורשה. Off for עוסק פטור.
                                    VAT is added ON TOP of the amount and persisted so the
                                    Hashavshevet export posts a שורת מע״מ to 250032 (מעמ תשומות). */}
                                {(() => {
                                    const currId =
                                        typeof values.currency === 'object'
                                            ? values.currency?._id
                                            : values.currency;
                                    if (String(currId) !== String(localCurrency)) return null;
                                    const vatRate = getVatPercent(clientsContext.selected);
                                    const net = Number(values.amount) || 0;
                                    const vat = values.chargeVat ? calcVatOnTop(net, vatRate) : 0;
                                    return (
                                        <>
                                            <Field
                                                type="checkbox"
                                                component={RenderInput}
                                                name="chargeVat"
                                                label="Charge VAT (חייב מע״מ)"
                                                disabled={isSubmitting}
                                                fullWidth
                                            />
                                            {values.chargeVat && (
                                                <Box sx={{ px: 1, color: 'text.secondary', fontSize: 13 }}>
                                                    VAT ({vatRate}%): {taytanFormatNumberToString(vat)} — posts
                                                    to מעמ תשומות (250032)
                                                    <br />
                                                    Total to pay:{' '}
                                                    {taytanFormatNumberToString(
                                                        Math.round((net + vat) * 100) / 100
                                                    )}
                                                </Box>
                                            )}
                                        </>
                                    );
                                })()}
                                <Field
                                    component={RenderInput}
                                    label="Remark"
                                    name="remark"
                                    multiline
                                    rows={2}
                                    disabled={isSubmitting}
                                    fullWidth
                                    margin="none"
                                />
                            </Stack>
                        </CollapsibleSection>
                    </Grid>
                    <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
                    <Grid container item direction="column" xs={12} sm>
                        <CollapsibleSection label="Payment Method" card>
                            <FormControl variant="standard" disabled={isSubmitting}>
                                <RadioGroup
                                    row
                                    defaultValue={paymentMethodTypeEnum.CREDIT_CARD}
                                    name="paymentMethodType"
                                    onChange={(ev) => {
                                        setPaymentMethodType(ev.target.value as PaymentMethodTypes);
                                        setFieldValue('paymentMethodType', ev.target.value);
                                    }}
                                >
                                    {paymentMethodTypesOptionsWithoutVoucher.map((paymentMethodType) => {
                                        return (
                                            <FormControlLabel
                                                key={paymentMethodType}
                                                control={<Radio />}
                                                value={paymentMethodType}
                                                label={paymentMethodType}
                                            />
                                        );
                                    })}
                                </RadioGroup>
                            </FormControl>
                            <Grid item xs={12}>
                                {values.paymentMethodType === paymentMethodTypeEnum.CREDIT_CARD && (
                                    <>
                                        {/* A card REPAY returns money to the card that paid, so
                                            nothing here is a choice: the card, the terminal and the
                                            original charge are all fixed by the receipt. Showing them
                                            read-only is what TB's Remittance Advice does, and it is
                                            what lets the agent confirm they are refunding the right
                                            charge before money moves.

                                            The terminal picker is deliberately hidden — money can
                                            only go back through the terminal that took it, so
                                            offering a choice would only allow a repay that cannot
                                            settle. */}
                                        {values.isRepay ? (
                                            <RepayCardDetails documentGenerationData={documentGenerationData} />
                                        ) : (
                                            <>
                                                <PaymentsTerminalField disabled={isSubmitting} />
                                                <CreditCardPaymentPlanFields
                                                    tripDate={tripDate}
                                                    fullPaymentWindowDays={fullPaymentWindowDays}
                                                    disabled={isSubmitting}
                                                />
                                            </>
                                        )}
                                        {isNextToMasleka && (
                                            <CreditCardSubForm
                                                open={open}
                                                toggleOpen={onClose}
                                                isLoading={false}
                                                values={values}
                                                documentGenerationData={documentGenerationData}
                                                tripDate={tripDate}
                                                resumeRemittance
                                                selectedDocuments={selectedDocuments}
                                                onPaymentSuccess={onCardPaymentSuccess}
                                            />
                                        )}
                                    </>
                                )}
                                {values.paymentMethodType === paymentMethodTypeEnum.BANK_TRANSFER && (
                                    <RemittanceBankTransferSubForm
                                        formProps={formProps}
                                        showRateOverrideCheckbox
                                    />
                                )}
                                {values.paymentMethodType === paymentMethodTypeEnum.CASH && (
                                    <Field
                                        component={RenderInput}
                                        label="Details"
                                        name="paymentMethodObject.details"
                                        multiline
                                        rows={3}
                                        fullWidth
                                        disabled={isSubmitting}
                                    />
                                )}
                                {values.paymentMethodType === paymentMethodTypeEnum.CHECK && (
                                    <>
                                        <FormFieldArray
                                            fieldName="paymentMethodObject.checks"
                                            getEmptyRow={getEmptyCheck}
                                            rowFields={RowCheckFields}
                                            label="Check"
                                            collapsible
                                            getRowSummary={getCheckSummary(values.currency?.symbol)}
                                            propsToRowFields={{
                                                currencySymbol: values.currency?.symbol,
                                                // amount * rate = local (see RateControlBox), so
                                                // this equivalent is a LOCAL figure and takes the
                                                // local symbol. Suppressed entirely for a
                                                // local-currency document — see the helper.
                                                getExtraRowAmountLabel: getCheckLocalAmountLabel(
                                                    values.currency,
                                                    localCurrency,
                                                    values?.rate,
                                                    currenciesContext.getSymbolByCurrency(localCurrency) || ''
                                                )
                                            }}
                                        />
                                        <Typography variant="body2" sx={{ mt: 1 }}>
                                            {`Remittance total (sum of checks): ${taytanFormatNumberToString(
                                                checksTotal
                                            )}${values.currency?.symbol || ''}`}
                                        </Typography>
                                        {checksMismatch && (
                                            <Typography
                                                variant="body2"
                                                sx={{ mt: 0.5, color: 'warning.main' }}
                                            >
                                                {`Does not match the amount to remit of ${taytanFormatNumberToString(
                                                    coveredTotal
                                                )}${values.currency?.symbol || ''}.`}
                                            </Typography>
                                        )}
                                        <Field
                                            type="checkbox"
                                            component={RenderInput}
                                            name="paymentMethodObject.overrideRateByValueDate"
                                            label="Override rate by first check's value date"
                                            disabled={isSubmitting}
                                        />
                                    </>
                                )}
                            </Grid>
                        </CollapsibleSection>
                    </Grid>
                </Grid>
                {isSubmitting && <Spinner />}
            </Box>
        </>
    );
};

export default memo(RemittanceForm);
